import React, { useState } from 'react';
import { MessageCircleIcon, PhoneIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { faqCategories, faqs } from '../data/faqs';
import { site } from '../data/site';
import type { FaqCategory } from '../types/content';
import { buttonStyles, container, focusRing } from '../utils/styles';
import { PageHeader } from '../components/shared/PageHeader';
import { FaqAccordion } from '../components/shared/FaqAccordion';
export function Faq() {
  const {
    t
  } = useLanguage();
  useDocumentMeta('faq');
  const [category, setCategory] = useState<FaqCategory | 'all'>('all');
  const visible = category === 'all' ? faqs : faqs.filter(f => f.category === category);
  return <>
      <PageHeader crumb={t({
      en: 'FAQ',
      es: 'Preguntas'
    })} title={t({
      en: 'Frequently Asked Questions',
      es: 'Preguntas Frecuentes'
    })} intro={t({
      en: 'Everything families usually ask about getting started, services, pricing, and scheduling. Switch the language anytime at the top of the page.',
      es: 'Todo lo que las familias suelen preguntar sobre cómo empezar, servicios, precios y horarios. Cambia el idioma en cualquier momento en la parte superior.'
    })} data-id="element-530" data-mp="3psqblwfwadxn" />

      <section className="bg-white py-16 sm:py-20" data-id="element-531" data-mp="2k4hrgz2fstl1">
        <div className={`${container} grid gap-12 lg:grid-cols-12 lg:gap-16`} data-id="element-532" data-mp="2k1vc4d9b19s9">
          <div className="lg:col-span-8" data-id="element-533" data-mp="2p32koej9uczz">
            <div role="tablist" aria-label={t({
            en: 'Question categories',
            es: 'Categorías de preguntas'
          })} className="flex flex-wrap gap-2" data-id="element-534" data-mp="2t4o0o8r3r7ee">
              {faqCategories.map(c => {
              const active = c.id === category;
              return <button key={c.id} type="button" role="tab" aria-selected={active} onClick={() => setCategory(c.id)} className={`min-h-[44px] whitespace-nowrap rounded-full px-5 font-medium transition-colors duration-150 ease-out ${focusRing} ${active ? 'bg-brand text-white' : 'bg-cream text-ink hover:bg-brand-tint hover:text-brand-dark'}`} data-id="element-535" data-mp="32wtt1jl9nqu4">
                    {t(c.label)}
                  </button>;
            })}
            </div>
            <div className="mt-8" role="tabpanel" data-id="element-536" data-mp="35upxv0vhrikn">
              <FaqAccordion key={category} items={visible} data-id="element-537" data-mp="35tekqag5oygv" />
            </div>
          </div>

          <aside className="lg:col-span-4" data-id="element-538" data-mp="30vwjl68drnjf">
            <div className="rounded-2xl bg-brand-dark p-8 text-white lg:sticky lg:top-36" data-id="element-539" data-mp="3ft5do7p2j5eu">
              <h2 className="text-2xl font-bold" data-id="element-540" data-mp="3auc954rreoqt">{t({
                en: 'Still have questions?',
                es: '¿Tienes más preguntas?'
              })}</h2>
              <p className="mt-3 text-white/85" data-id="element-541" data-mp="1lembk68fv3xc">
                {t({
                en: 'Talk to a real person, any time of day. We’re happy to help.',
                es: 'Habla con una persona real, a cualquier hora. Con gusto te ayudamos.'
              })}
              </p>
              <a href={site.tel} className={`mt-5 block rounded font-display text-3xl font-bold hover:underline ${focusRing}`} data-id="element-542" data-mp="x3n7m8pv7kv">
                {site.phoneDisplay}
              </a>
              <div className="mt-6 grid gap-3" data-id="element-543" data-mp="3ncy83qiiu8m5">
                <a href={site.tel} className={buttonStyles.light} data-id="element-544" data-mp="3nbn35ndcautg">
                  <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-545" data-mp="39cnfoj1kclyv" />
                  {t({
                  en: 'Call Now',
                  es: 'Llamar Ahora'
                })}
                </a>
                <a href={site.whatsapp} target="_blank" rel="noopener noreferrer" className={buttonStyles.whatsapp} data-id="element-546" data-mp="3dg39l7m9087v">
                  <MessageCircleIcon className="h-5 w-5" aria-hidden="true" data-id="element-547" data-mp="2zhby7syrt0nf" />
                  WhatsApp
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>;
}